/**
 * The locale a subtree renders in (the i18n wiring, D7).
 *
 * **Two providers, nested, and the inner one is the point.** The outer one runs on the
 * player's own locale, from the profile: it is what the shelf, the settings screen and the
 * leaderboard read in. The inner one is mounted by `PlayRoute` on `story.locale`, so
 * everything inside a story — the panel, the codex, the ticker, the buttons that write a
 * sentence into the action box — is in the language the DM is writing in.
 *
 * That split is not a nicety. A story fixes its language when it is created and keeps it for
 * its whole life (`StoryCreate.locale`, "Fixes the DM's language for the story's life"), and
 * there is deliberately no way to change it afterwards: the prompt cache, the chapters
 * already written and the DM's own recall are all in that one language. A player whose
 * profile says `de` reading a story they started in `en` should get an English interface
 * around English prose, not a German frame around it. `users.locale` decides what a *new*
 * story will be and what the surfaces outside a story read in; `stories.locale` decides
 * everything else.
 *
 * Nothing here translates content. Narration, choices, item names, injury descriptions and
 * the summary all arrive in the story's language already — the DM writes them that way and
 * `quest/api/display.py` joins the rest server-side. This is chrome only.
 */
import { useEffect, useMemo, type ReactNode } from 'react';
import { matchPath, useLocation, useMatch } from 'react-router';
import type { StoryDetail } from '@/api/generated';
import { useProfile, useStory, useTranslations } from '@/api/queries';
import { useAuth } from '@/auth/context';
import { I18nContext, useT } from '@/i18n/context';
import type { UiKey } from '@/i18n/en.generated';
import { intlLocale, makeTranslator, type Translate } from '@/i18n/t';

interface I18nProviderProps {
  /** `undefined` while the profile or the story is still loading — `en` stands in. */
  locale: string | undefined;
  children: ReactNode;
}

const NO_STRINGS: Readonly<Record<string, string>> = {};

export function I18nProvider({ locale, children }: I18nProviderProps) {
  const resolved = locale ?? 'en';

  // `en` is bundled, so asking for it is a round trip to be told what is already compiled in.
  // Nothing else gates the fetch: this component deliberately knows nothing about auth, so it
  // can be mounted by `PlayRoute` — and by a test — with a locale and nothing else. Not
  // fetching when signed out is `AppI18nProvider`'s job, because it is the one that knows.
  const { data } = useTranslations(resolved === 'en' ? undefined : resolved);

  // Keyed on the locale and the payload, so the whole tree does not re-render on an
  // unrelated Query cache write. The strings for a locale never change within a session.
  const strings = data?.locale === resolved ? data.strings : NO_STRINGS;
  const translate = useMemo(() => makeTranslator(resolved, strings), [resolved, strings]);

  return <I18nContext value={translate}>{children}</I18nContext>;
}

/**
 * The outer provider — the player's own locale, for everything outside a story.
 *
 * It sits above the router rather than inside it so the locale does not reset between
 * routes, which means it is also above the guard: `/login` and `/leaderboard` render
 * through it signed out, and there the answer is simply English. No `navigator.language`
 * guess — the profile is the only place the player has actually said what they read, and
 * guessing at the door only to correct it a moment later is a visible flicker in exchange
 * for nothing.
 */
export function AppI18nProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const signedIn = status === 'signed_in';
  const { data } = useProfile(signedIn);

  // `signedIn` gates the *locale*, not just the fetch: a profile left in the cache by a
  // session that has since ended would otherwise send a tokenless `/translations` request on
  // the sign-in screen, and answer a 401 with the English it was going to render anyway.
  return <I18nProvider locale={signedIn ? data?.locale : undefined}>{children}</I18nProvider>;
}

/**
 * The locale of the **screen**, for the chrome that outlives the route.
 *
 * `AppI18nProvider` sits above the router so the language does not reset between routes,
 * which means it cannot see which route is mounted — and the global chrome `App` renders
 * beside `<Routes>` (the feedback button and its form, the toasts, the offline notice)
 * inherits from it. That was right while those controls hung off the pages, and wrong the
 * moment they became global: the feedback button floats over the story screen, in the
 * corner of a page that is otherwise entirely in the story's language, and read `feedback`
 * in English at a player who had chosen `ua`.
 *
 * So this one resolves the same rule `PlayRoute` applies, from inside the router: on
 * `/play/:storyId` the story decides, everywhere else the profile does. `useStory` is the
 * query `PlayRoute` has already run, so on the story screen this is a cache read rather
 * than a second request — and subscribing to it rather than peeking at the cache is what
 * makes the chrome switch language when the story lands.
 *
 * Having resolved that rule, it is also the only component that can answer for the document
 * head, so both halves of it hang here: `<html lang>` (L10.1) and `<title>` (L10.2).
 */
export function ScreenI18nProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const signedIn = status === 'signed_in';
  const { data: profile } = useProfile(signedIn);
  const match = useMatch('/play/:storyId');
  const { data: story } = useStory(signedIn ? match?.params.storyId : undefined);

  const locale = story?.locale ?? (signedIn ? profile?.locale : undefined);
  useDocumentLang(locale);

  return (
    <I18nProvider locale={locale}>
      {/* Inside the provider rather than beside it: unlike `lang`, the title is made of
          words, so it needs the translator this renders. Same single-owner argument. */}
      <DocumentTitle story={story} />
      {children}
    </I18nProvider>
  );
}

/**
 * `<html lang>` — the one string a screen reader reads before any of the prose (L10.1).
 *
 * `index.html` ships `lang="en"` and is one static document behind CloudFront, so without
 * this a player reading a German story is announced with English phonemes for the entire
 * session — every chapter, every item name, every choice. That is an accessibility bug
 * rather than a translation gap: the words are already German, only the pronunciation is
 * wrong. It is also the half of the head worth fixing from JavaScript, because no crawler
 * is involved; the `og:` block stays English and `index.html` says why.
 *
 * **`document.documentElement` is a singleton, so exactly one component owns it**, and this
 * is that component. Setting it inside `I18nProvider` instead would put three writers on one
 * attribute — the app provider, this one, and the one `PlayRoute` mounts on `story.locale` —
 * and effects run child-first, so the outermost and least specific writer would land last
 * and win. That is precisely backwards. `ScreenI18nProvider` already resolves the rule those
 * three exist to express (on `/play/:storyId` the story decides, everywhere else the
 * profile does), which makes it the one place that can answer for the whole document.
 *
 * `intlLocale` for the same reason every `Intl` call needs it: the API's tag for Ukrainian
 * is `ua`, and `lang="ua"` is an unassigned subtag that a screen reader cannot act on. `uk`
 * is the BCP-47 tag, and this attribute is read by exactly the software that cares.
 *
 * Nothing is restored on unmount — this is mounted for the app's life, and the last locale
 * rendered is still the right answer for whatever is left on the screen.
 */
function useDocumentLang(locale: string | undefined): void {
  // `en` while the profile or the story is still in flight, matching what `I18nProvider`
  // renders in. Announcing English prose in English is correct; it is the *story* screen
  // that has to change, and it does so as soon as `useStory` answers.
  const tag = intlLocale(locale ?? 'en');

  useEffect(() => {
    document.documentElement.lang = tag;
  }, [tag]);
}

/**
 * The route each path's tab is named after (L10.2).
 *
 * Every one of these is a string the page already renders as its own `<h1>`, and that is the
 * point — a tab that says something the page does not is a second name to keep in step.
 * `/stories` is the exception: its heading is the app's name, so it needs a title of its
 * own, and `ui.nav.your_stories` is not it — the nav labels are deliberately lowercase
 * fragments meant to sit inside a sentence, and title case is not something code can add on
 * a locale's behalf.
 *
 * `matchPath` rather than an object keyed by pathname, so `/stories/` names the shelf like
 * the router says it does rather than falling through to the 404 title.
 */
const TITLE_KEYS: ReadonlyArray<readonly [path: string, key: UiKey]> = [
  ['/login', 'ui.auth.sign_in'],
  ['/leaderboard', 'ui.leaderboard.title'],
  ['/stories', 'ui.title.stories'],
  ['/stories/new', 'ui.character.create'],
  ['/settings', 'ui.settings.title'],
];

/**
 * `document.title` — the other half of the head a player actually reads (L10.2).
 *
 * `index.html` ships one static `<title>Wasteland</title>` to every reader, and the `og:`
 * block beside it stays that way on purpose: rewriting metadata from JavaScript hands a
 * crawler the wrong thing, and there is no server to render it per locale. **The tab is not
 * metadata.** It is chrome the player looks at, in a window they may have four of, and no
 * crawler is involved — the same split L10.1 made for `lang`.
 *
 * **One owner, for the reason L10.1 found the hard way**: `document.title` is a singleton,
 * and per-route `useEffect`s writing it on mount race on every navigation — the leaving
 * route's cleanup and the arriving route's effect are one commit apart, and a redirect like
 * `/` makes that two commits. Deriving it from `useLocation` in the single component that
 * already resolves the screen's locale means the title is a *render* of the current path
 * rather than a side effect scheduled by whoever mounted last.
 *
 * A story's tab carries the character's name, which is what the shelf calls it and what a
 * player is choosing between when four tabs are open. It is content, so it needs no key —
 * and until `useStory` answers there is no honest name, so the bare app title stands in
 * rather than a flash of a placeholder.
 */
function DocumentTitle({ story }: { story: StoryDetail | undefined }) {
  const t = useT();
  const { pathname } = useLocation();

  const app = t('ui.app.title');
  const page = resolvePage(t, pathname, story);
  const title = page ? t('ui.title.page', { page, app }) : app;

  useEffect(() => {
    document.title = title;
  }, [title]);

  return null;
}

/** `undefined` where the app's own name is the whole title. */
function resolvePage(t: Translate, pathname: string, story: StoryDetail | undefined) {
  // `/` is a redirect that renders no page of its own, and `/play/:storyId` before the story
  // lands is a screen with nothing to be named after yet.
  if (matchPath('/', pathname)) return undefined;
  if (matchPath('/play/:storyId', pathname)) return story?.character_name;

  const entry = TITLE_KEYS.find(([path]) => matchPath(path, pathname));
  // No entry is the 404 route, which is a page like any other and says so.
  return t(entry?.[1] ?? 'ui.error.not_found_title');
}
